import React, { useState, useEffect, useRef, startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import UserProfileDropdown from '../UserProfileDropdown/UserProfileDropdown';
import { onboardingAPI } from '../../features/onboarding/api/onboardingAPI';
import { ONBOARDING_COMPLETE_EVENT } from '../../features/apps/components/ChatShell';
import { CASE_STUDY_ACTIVE_EVENT } from '../../features/apps/components/AppExperience';
import { isInstructorAccount } from '../../utils/isInstructorAccount';
import './Sidebar.css';

const LOCK_ICON = (
  <svg className="footer-lock-icon" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="1.5" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const CHAT_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const APPS_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
  </svg>
);

const CASES_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const MENU_ITEMS = [
  { path: '/chat', label: 'Chat', lockable: false, icon: CHAT_ICON },
  { path: '/apps', label: 'Apps', lockable: true, icon: APPS_ICON },
  { path: '/cases', label: 'Cases', lockable: true, icon: CASES_ICON },
];

const ONBOARDING_COMPLETE_KEY = 'pathbuddy_onboarding_complete';

function readCachedOnboardingComplete() {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_COMPLETE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch (_) {
    /* ignore */
  }
  return null; // unknown — do not show footer locks until confirmed
}

const Sidebar = ({ isMobileOpen, onClose, isOpen: externalIsOpen, onToggleOpen }) => {
  const { user, isAuthenticated } = useAuth();
  const instructorCasesPermitted = isAuthenticated && isInstructorAccount(user);
  const navigate = useNavigate();
  const location = useLocation();

  const [internalIsOpen, setInternalIsOpen] = useState(() => {
    const saved = localStorage.getItem('footer-menu-open');
    return saved !== null ? saved === 'true' : true;
  });
  const [onboardingComplete, setOnboardingComplete] = useState(readCachedOnboardingComplete);
  const [caseStudyActive, setCaseStudyActive] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastLabel, setToastLabel] = useState('');
  const toastTimeoutRef = useRef(null);
  // Instant visual active state while startTransition finishes navigating
  // ChatShell's heavy Apps/Cases panes (avoids grey/stuck press on Cases).
  const [pendingTabPath, setPendingTabPath] = useState(null);

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  
  useEffect(() => {
    // caseStudyActive also gates this, not just isOpen — otherwise the
    // reserved --footer-height space (and taskbar body classes) would stay
    // applied even while the footer itself renders nothing (returns null
    // below), leaving a blank gap on the case-study step.
    const visuallyOpen = isOpen && !caseStudyActive;
    // The footer bar sits above the iOS home-indicator inset, so its real
    // occupied height is 64px plus whatever that inset is -- not just 64px.
    // The env() fallback (0px) keeps this identical to today's behavior on
    // any browser/device that doesn't resolve it, so this is safe to ship
    // without an on-device check, though iOS Safari confirmation is still
    // worth doing.
    document.documentElement.style.setProperty(
      '--footer-height',
      visuallyOpen ? 'calc(64px + env(safe-area-inset-bottom, 0px))' : '0px'
    );
    document.body.classList.toggle('menu-taskbar-open', visuallyOpen);
    document.body.classList.toggle('menu-taskbar-closed', !visuallyOpen);
    return () => {
      document.documentElement.style.setProperty('--footer-height', '0px');
      document.body.classList.remove('menu-taskbar-open', 'menu-taskbar-closed');
    };
  }, [isOpen, caseStudyActive]);

  useEffect(() => {
    onboardingAPI.getState()
      .then((stateRes) => {
        const done = Boolean(stateRes.onboarding_completed_at);
        setOnboardingComplete(done);
        try {
          sessionStorage.setItem(ONBOARDING_COMPLETE_KEY, done ? 'true' : 'false');
        } catch (_) {
          /* ignore */
        }
      })
      .catch(() => {});

    const unlock = () => {
      setOnboardingComplete(true);
      try {
        sessionStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener(ONBOARDING_COMPLETE_EVENT, unlock);
    return () => window.removeEventListener(ONBOARDING_COMPLETE_EVENT, unlock);
  }, []);

  // Hides the footer only while the case-study step of the /apps-store/:id
  // /experience flow is active. The Chat/Apps/Cases footer is already
  // path-gated to those three routes only; this remains as a safety net
  // if that gate changes.
  useEffect(() => {
    const handleCaseStudyActiveChange = (e) => setCaseStudyActive(Boolean(e.detail?.active));
    window.addEventListener(CASE_STUDY_ACTIVE_EVENT, handleCaseStudyActiveChange);
    return () => window.removeEventListener(CASE_STUDY_ACTIVE_EVENT, handleCaseStudyActiveChange);
  }, []);

  // Clear lock toast when changing tabs so it never sticks over Chat/Apps/Cases
  useEffect(() => {
    setToastVisible(false);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setPendingTabPath(null);
  }, [location.pathname]);

  const userName = user?.name || user?.first_name || (user?.email ? user.email.split('@')[0] : 'Guest');
  const userEmail = user?.email || '';

  const showToast = (label) => {
    setToastLabel(label);
    setToastVisible(true);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToastVisible(false), 2200);
  };

  const isItemLocked = (item) => {
    if (item.path === '/cases' && instructorCasesPermitted) return false;
    // Only lock when we know onboarding is incomplete (null = unknown → no flash)
    return item.lockable && onboardingComplete === false;
  };

  const handleLinkClick = (item) => {
    const locked = isItemLocked(item);
    if (locked) {
      showToast(item.label);
      return;
    }
    // Chat, Apps and Cases share one ChatShell. A tab click only changes the
    // path, so the conversation, Paths query and sidebar the user left are
    // still there when they come back. ChatShell puts the query back on
    // /chat. A new chat is the New chat action in the panel, not this tab.
    // Optimistic active state + startTransition keeps the tab click feeling
    // instant while ChatShell swaps the heavy Apps/Cases panes.
    setPendingTabPath(item.path);
    startTransition(() => {
      navigate(item.path);
    });
    if (onClose) onClose();
  };

  const isLinkActive = (path) => {
    if (pendingTabPath) {
      return pendingTabPath === path;
    }
    if (path === '/apps-store') {
      return location.pathname.startsWith('/apps-store');
    }
    return location.pathname === path;
  };

  if (caseStudyActive) return null;

  return (
    <>
      <footer className={`app-sidebar footer-bar ${isOpen ? 'open' : 'closed'}`}>
        <div className="footer-content-inner">
          <div className="sidebar-footer-profile">
            <UserProfileDropdown
              userName={userName}
              userEmail={userEmail}
              variant="sidebar"
            />
          </div>

          <nav className="sidebar-nav-container" aria-label="Primary">
            <div className="sidebar-links-list">
              {MENU_ITEMS.map((item) => {
                const active = isLinkActive(item.path);
                const locked = isItemLocked(item);
                return (
                  <button
                    key={item.path}
                    type="button"
                    className={`sidebar-link-btn${active ? ' active' : ''}${locked ? ' locked' : ''}`}
                    onClick={() => handleLinkClick(item)}
                    aria-label={item.label}
                    aria-current={active ? 'page' : undefined}
                    aria-disabled={locked || undefined}
                  >
                    <span className="sidebar-link-icon">{item.icon}</span>
                    <span className="sidebar-link-label">{item.label}</span>
                    {locked && LOCK_ICON}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Balances the profile so Chat/Apps/Cases stay screen-centred */}
          <div className="sidebar-footer-balance" aria-hidden="true" />
        </div>
      </footer>

      <div className={`footer-lock-toast${toastVisible ? ' show' : ''}`} role="status" aria-live="polite">
        {LOCK_ICON}
        <span>Finish setting up to unlock {toastLabel}</span>
      </div>
    </>
  );
};

export default Sidebar;
